import skullIcon from '../../../shared/assets/button-icons/death.png';

/**
 * Props:
 *   tracker    – tracker object (id, name, size, value, locked, color)
 *   changeInfo – optional { oldValue, newValue } describing the change just made
 *   variant    – 'announcement' (default) | 'display'
 *
 * variant='announcement': red-border toast for GM/player views; full verbiage title + pips.
 * variant='display': dark persistent toast for the Display screen.
 *   - Fear:   skulls only (no title text)
 *   - Custom: tracker name + circle pips (no verbiage)
 */
function TrackerToastContent({
  tracker,
  changeInfo,
  variant = 'announcement',
  mode = 'dark',
}) {
  const isDark = mode !== 'light';
  const isFear = tracker.locked;
  const delta = changeInfo ? changeInfo.newValue - changeInfo.oldValue : 0;
  const hasChange = !!changeInfo && delta !== 0;
  const showTitle = !isFear || variant !== 'display';

  // ── Display variant for custom trackers: just "Name + circles" ────────────
  // Fear trackers with variant='display' fall through to the shared rendering
  // below (same title + skulls), so only custom trackers need an early return here.
  if (variant === 'display' && !isFear) {
    const color = tracker.color || '#e94f37';
    return (
      <div
        className={
          `tracker_toast_content` +
          (isDark ? ' tracker_toast_content_dark' : ' tracker_toast_content_light')
        }
      >
        <span
          className={
            `tracker_toast_title` +
            (isDark ? ' tracker_toast_title_dark' : ' tracker_toast_title_light')
          }
        >
          {tracker.name}
        </span>
        <div className='tracker_toast_pips'>
          {Array.from({ length: tracker.size }, (_, i) => (
            <span
              key={i}
              className={
                `tracker_toast_circle` +
                (isDark ? ' tracker_toast_circle_dark' : ' tracker_toast_circle_light')
              }
              style={
                i < tracker.value
                  ? { background: color, borderColor: color }
                  : undefined
              }
              aria-hidden='true'
            />
          ))}
        </div>
      </div>
    );
  }

  // ── Build title line ──────────────────────────────────────────────────────
  let title = isFear ? 'Fear' : tracker.name;
  if (hasChange) {
    const absDelta = Math.abs(delta);
    if (isFear) {
      if (delta < 0) {
        title =
          absDelta === 1
            ? 'The Game Master spends a Fear'
            : `The Game Master spends ${absDelta} Fear`;
      } else {
        title =
          absDelta === 1
            ? 'The Game Master gains a Fear'
            : `The Game Master gains ${absDelta} Fear`;
      }
    } else {
      const name = tracker.name || 'Tracker';
      if (delta > 0) {
        title =
          absDelta === 1
            ? `The Game Master ticked up ${name} by one stage`
            : `The Game Master ticked up ${name} by ${absDelta} stages`;
      } else {
        title =
          absDelta === 1
            ? `The Game Master ticked down ${name} by one stage`
            : `The Game Master ticked down ${name} by ${absDelta} stages`;
      }
    }
  }

  // ── Determine styling for each pip ───────────────────────────────────────
  // Fear (spending): filled pips are yellow to show what is being spent.
  // Fear (gaining / no change): filled pips are the normal white/filled style.
  // Custom (ticking down): pips in the range [newValue, oldValue) are shown as
  //   red circles (the ones just removed); all other filled pips use tracker color.
  
  const pips = Array.from({ length: tracker.size }, (_, i) => {
    const isFilled = i < tracker.value;
    const isChangedPip =
      !isFear &&
      hasChange &&
      delta < 0 &&
      i >= tracker.value &&
      i < tracker.value + Math.abs(delta);

    return { index: i, filled: isFilled, redPip: isChangedPip };
  });

  return (
    <div
      className={
        `tracker_toast_content` +
        (isDark ? ' tracker_toast_content_dark' : ' tracker_toast_content_light')
      }
    >
      {showTitle && (
        <span
          className={
            `tracker_toast_title` +
            (isDark ? ' tracker_toast_title_dark' : ' tracker_toast_title_light')
          }
        >
          {title}
        </span>
      )}
      <div className='tracker_toast_pips'>
        {pips.map(({ index, filled, redPip }) => {
          if (isFear) {
            return (
              <img
                key={index}
                src={skullIcon}
                alt=''
                className={
                  `tracker_toast_skull` +
                  (filled
                    ? ' tracker_toast_skull_filled'
                    : ' tracker_toast_skull_empty') +
                  (isDark
                    ? ' tracker_toast_skull_dark'
                    : ' tracker_toast_skull_light')
                }
              />
            );
          }
          const color = tracker.color || '#e94f37';
          return (
            <span
              key={index}
              className={
                `tracker_toast_circle` +
                (isDark ? ' tracker_toast_circle_dark' : ' tracker_toast_circle_light')
              }
              style={
                redPip
                  ? { borderColor: '#e94f37' }
                  : filled
                    ? { background: color, borderColor: color }
                    : undefined
              }
              aria-hidden='true'
            />
          );
        })}
      </div>
    </div>
  );
}

export default TrackerToastContent;
